import { isPlatformBrowser } from '@angular/common';
import { httpResource } from '@angular/common/http';
import {
  computed,
  effect,
  Injectable,
  inject,
  PLATFORM_ID,
  type Signal,
  signal,
} from '@angular/core';

const STORAGE_LANG_KEY = 'lang';

export enum Language {
  ENGLISH = 'en',
  FRENCH = 'fr',
}

/** Locale registry, in priority order — the first entry is the ultimate fallback. */
export const SUPPORTED_LANGUAGES: readonly Language[] = [Language.ENGLISH, Language.FRENCH];

export interface LanguageResolutionInput {
  /** Tier 1 — the signed-in user's site-configured language, if any. */
  siteLanguage?: Language | null;
  /** Tier 2 — raw browser locale (e.g. "fr-FR"). */
  browserLanguage?: string | null;
  /** Tier 3 — a previously saved preference (e.g. from localStorage). */
  savedLanguage?: Language | null;
}

/**
 * 3-tier cascading language resolution: the site's configured language wins
 * if it's supported, otherwise the browser's language if supported,
 * otherwise a saved preference if supported, otherwise the first language
 * in the locale registry.
 */
export function resolveLanguage(
  input: LanguageResolutionInput,
  supported: readonly Language[] = SUPPORTED_LANGUAGES,
): Language {
  if (input.siteLanguage && supported.includes(input.siteLanguage)) {
    return input.siteLanguage;
  }

  const browserLang = input.browserLanguage?.split('-')[0];
  const matchedBrowserLang = supported.find((lang) => lang === browserLang);
  if (matchedBrowserLang) return matchedBrowserLang;

  if (input.savedLanguage && supported.includes(input.savedLanguage)) {
    return input.savedLanguage;
  }

  return supported[0];
}

@Injectable({ providedIn: 'root' })
export class TranslateService {
  #platformId = inject(PLATFORM_ID);
  #isBrowser = isPlatformBrowser(this.#platformId);

  public readonly supportedLanguages = SUPPORTED_LANGUAGES.map((lang) => ({ lang }));

  /**
   * Site config isn't known yet at construction time (auth hasn't run), so
   * the initial value only resolves tiers 2–4. Once the user signs in,
   * {@link resolveForSite} re-resolves with the site tier included.
   */
  public language = signal<Language>(
    resolveLanguage({
      browserLanguage: this.#isBrowser ? navigator.language : null,
      savedLanguage: this.#readSavedLanguage(),
    }),
  );

  #translationsResource = httpResource<Record<string, unknown>>(
    () => `assets/translations/${this.language()}.json`,
  );

  public translatedData = computed(() => this.#translationsResource.value() ?? {});
  public isLoading = this.#translationsResource.isLoading;

  constructor() {
    // Persist language choice whenever it changes, instead of doing it
    // manually inside setLanguage()
    effect(() => {
      const lang = this.language();
      if (this.#isBrowser) {
        localStorage.setItem(STORAGE_LANG_KEY, lang);
      }
    });
  }

  public getKeysTranslation(keys: string[], values?: string[][]): Signal<string> {
    return computed(() => {
      if (this.isLoading()) return '';
      return keys
        .map((key, index) =>
          this.#getTranslationForKey(this.translatedData(), key, values?.[index]),
        )
        .join('\n');
    });
  }

  public getKeyTranslation(key: string, values?: string[]): Signal<string> {
    return computed(() => {
      if (this.isLoading()) return '';
      return this.#getTranslationForKey(this.translatedData(), key, values);
    });
  }

  public setLanguage(lang: Language): void {
    this.language.set(lang);
  }

  /**
   * Re-runs the 3-tier cascade now that the signed-in user's site language
   * (tier 1) is known — called once right after login. A manual choice made
   * later in the same session (via {@link setLanguage}) is never overwritten
   * by this, since it only runs on sign-in.
   */
  public resolveForSite(siteLanguage: Language | null): void {
    this.language.set(
      resolveLanguage({
        siteLanguage,
        browserLanguage: this.#isBrowser ? navigator.language : null,
        savedLanguage: this.#readSavedLanguage(),
      }),
    );
  }

  #readSavedLanguage(): Language | null {
    if (!this.#isBrowser) return null;
    return localStorage.getItem(STORAGE_LANG_KEY) as Language | null;
  }

  #getTranslationForKey(data: Record<string, unknown>, key: string, values?: string[]): string {
    try {
      let result: unknown = data;
      for (const segment of key.split('.')) {
        result = (result as Record<string, unknown> | undefined)?.[segment];
        if (result === undefined) {
          console.warn(`Missing translate key [${key}]`);
          return key;
        }
      }

      if (typeof result !== 'string') return key;

      if (values) {
        return result.replace(/{(\d+)}/g, (match, indexStr: string) => {
          const value = values[Number(indexStr)];
          return value !== undefined ? value : match;
        });
      }
      return result;
    } catch {
      return key;
    }
  }
}
